import type { ImportMappingRow } from '@/integration/ImportMappingGrid';
import type {
  OnlineImportAnalysis,
  OnlineImportFormat,
  OnlineImportMapping,
  OnlineImportPreview,
  OnlineImportResult,
  OnlineImportTarget,
} from './onlineImportService';

/**
 * The online import panel's state, as a pure reducer (integration_architecture.md, Online import):
 * choose a file — it is read at once, and read again when how to read it changes — adjust its
 * columns if need be, preview what each row would do, import, see the outcome. A new file or a
 * changed format discards the analysis and the mapping built on it — they described another file —
 * and any change discards the preview: it described another import. Import needs a preview of the
 * import as it stands.
 */

export type OnlineImportStep = 'file' | 'preview' | 'result';

export const ONLINE_IMPORT_STEPS: OnlineImportStep[] = ['file', 'preview', 'result'];

/**
 * An import profile the panel starts from instead of the server's suggestion: its format, and its
 * mapping in place of the one suggested when the file is read. What the panel changes is not
 * saved to the profile.
 */
export interface OnlineImportPreset {
  /** The profile's name. */
  name: string;
  format: OnlineImportFormat;
  mappings: OnlineImportMapping[];
}

export interface OnlineImportState {
  step: OnlineImportStep;
  file: File | null;
  format: OnlineImportFormat;
  analysis: OnlineImportAnalysis | null;
  /** The workbook's sheets, kept while the file is read again with other settings. */
  worksheets: string[];
  mappings: ImportMappingRow[];
  preview: OnlineImportPreview | null;
  result: OnlineImportResult | null;
  /** The profile the panel started from; it outlives a new file, a new format and a reset. */
  preset: OnlineImportPreset | null;
  /**
   * Counts the changes that make a reading or a preview describe something else. A request carries
   * the revision it started at, and its answer is dropped when the revision has moved on — the
   * header choice changed while the file was being read, or the panel was closed meanwhile.
   */
  revision: number;
}

export type OnlineImportEvent =
  | { type: 'fileChosen'; file: File }
  | { type: 'fileRemoved' }
  | { type: 'formatChanged'; format: Partial<OnlineImportFormat> }
  | { type: 'analyzed'; analysis: OnlineImportAnalysis; revision: number }
  | { type: 'mappingsChanged'; mappings: ImportMappingRow[] }
  | { type: 'previewed'; preview: OnlineImportPreview; revision: number }
  | { type: 'goTo'; step: OnlineImportStep }
  | { type: 'imported'; result: OnlineImportResult }
  | { type: 'reset' };

export const INITIAL_FORMAT: OnlineImportFormat = { hasHeader: true };

/** The panel before a file is chosen, from `preset` when it starts from a profile. */
export function initialState(preset: OnlineImportPreset | null = null): OnlineImportState {
  return {
    step: 'file',
    file: null,
    format: preset?.format ?? INITIAL_FORMAT,
    analysis: null,
    worksheets: [],
    mappings: [],
    preview: null,
    result: null,
    preset,
    revision: 0,
  };
}

export const INITIAL_STATE: OnlineImportState = initialState();

type Handler<T extends OnlineImportEvent['type']> =
  (state: OnlineImportState, event: Extract<OnlineImportEvent, { type: T }>) => OnlineImportState;

/** One handler per event: each event's rule on its own, rather than one branch per event in a single function. */
const HANDLERS: { [T in OnlineImportEvent['type']]: Handler<T> } = {
  fileChosen: (state, event) => ({ ...startOver(state), file: event.file }),
  fileRemoved: startOver,
  reset: startOver,
  formatChanged: (state, event) => ({
    ...state,
    step: 'file',
    format: { ...state.format, ...event.format },
    analysis: null,
    mappings: [],
    preview: null,
    result: null,
    revision: state.revision + 1,
  }),
  analyzed: (state, event) => (event.revision === state.revision ? analyzed(state, event.analysis) : state),
  mappingsChanged: (state, event) => ({ ...state, mappings: event.mappings, preview: null, revision: state.revision + 1 }),
  previewed: (state, event) => (event.revision === state.revision ? { ...state, step: 'preview', preview: event.preview } : state),
  goTo: (state, event) => ({ ...state, step: event.step }),
  imported: (state, event) => ({ ...state, step: 'result', result: event.result }),
};

export function onlineImportReducer(state: OnlineImportState, event: OnlineImportEvent): OnlineImportState {
  const handle = HANDLERS[event.type] as (state: OnlineImportState, event: OnlineImportEvent) => OnlineImportState;
  return handle(state, event);
}

/** The panel as it opened, still counting, so an answer to a request made before is dropped. */
function startOver(state: OnlineImportState): OnlineImportState {
  return { ...initialState(state.preset), revision: state.revision + 1 };
}

function analyzed(state: OnlineImportState, analysis: OnlineImportAnalysis): OnlineImportState {
  return {
    ...state,
    analysis,
    worksheets: analysis.worksheets,
    format: { ...state.format, worksheetName: analysis.worksheet ?? state.format.worksheetName },
    mappings: (state.preset?.mappings ?? analysis.suggestedMappings).map(toRow),
    preview: null,
  };
}

function toRow(mapping: OnlineImportMapping, index: number): ImportMappingRow {
  return {
    id: String(index),
    sourceColumn: mapping.sourceColumn,
    targetField: mapping.targetField ?? '',
    transformChain: mapping.transformChain ?? '',
    defaultValue: mapping.defaultValue ?? '',
  };
}

/** The file needs reading: chosen, and not read the way it is now to be read. */
export function needsAnalysis(state: OnlineImportState): boolean {
  return !!state.file && !state.analysis;
}

/** The mappings the server receives: the rows that fill a field. */
export function mappingsToSend(rows: ImportMappingRow[]): OnlineImportMapping[] {
  return rows
    .filter((row) => row.sourceColumn && row.targetField)
    .map((row) => ({
      sourceColumn: row.sourceColumn,
      targetField: row.targetField,
      transformChain: row.transformChain || null,
      defaultValue: row.defaultValue || null,
    }));
}

/** CSV or XLSX, from the file's name, before the server has read it. */
export function isWorkbook(file: File | null): boolean {
  return !!file && /\.xlsx$/i.test(file.name);
}

/**
 * The file's columns: its header row, or — for a CSV read without one — the names the server gives
 * the rows' cells (`Column0`, `Column1`, …), which a mapping of such a file uses.
 */
export function fileColumns(analysis: OnlineImportAnalysis): string[] {
  if (analysis.headers.length > 0) return analysis.headers;
  const names = new Set<string>();
  analysis.sampleRows.forEach((row) => Object.keys(row).forEach((name) => names.add(name)));
  return [...names];
}

/**
 * The column the server reads `sourceColumn` from, by the server's own rule (CsvFieldMapper
 * .TryGetSource): the same name ignoring case, else the same name ignoring case, underscores,
 * hyphens and spaces. Null when the file has no such column, so every row reads it empty.
 */
export function columnFor(columns: string[], sourceColumn: string): string | null {
  const exact = columns.find((column) => sameIgnoringCase(column, sourceColumn));
  if (exact !== undefined) return exact;
  const wanted = withoutSeparators(sourceColumn);
  return columns.find((column) => sameIgnoringCase(withoutSeparators(column), wanted)) ?? null;
}

const withoutSeparators = (name: string) => name.replace(/[_\- ]/g, '');

/**
 * .NET's OrdinalIgnoreCase: each character upper-cased on its own, so a character whose capital is
 * two characters (ß) compares as itself, as it does on the server.
 */
function sameIgnoringCase(a: string, b: string): boolean {
  return a.length === b.length && upperEach(a) === upperEach(b);
}

function upperEach(value: string): string {
  return Array.from(value, (char) => {
    const upper = char.toUpperCase();
    return upper.length === char.length ? upper : char;
  }).join('');
}

/** One column the mapping reads, and whether the file has it. */
export interface ColumnMatch {
  sourceColumn: string;
  found: boolean;
}

/** The columns the mapping reads, each once, in the mapping's order, found in the file or not. */
export function columnMatches(analysis: OnlineImportAnalysis, rows: ImportMappingRow[]): ColumnMatch[] {
  const columns = fileColumns(analysis);
  const sources = [...new Set(mappingsToSend(rows).map((mapping) => mapping.sourceColumn))];
  return sources.map((sourceColumn) => ({ sourceColumn, found: columnFor(columns, sourceColumn) !== null }));
}

/**
 * Columns the mapping reads that the file's first row names, when that row is read as data: a CSV
 * read without a header whose header is in fact there. Empty otherwise.
 */
export function headerReadAsData(state: OnlineImportState): string[] {
  if (!state.analysis || state.format.hasHeader || isWorkbook(state.file)) return [];
  const firstRow = Object.values(state.analysis.sampleRows[0] ?? {});
  return columnMatches(state.analysis, state.mappings)
    .filter((match) => !match.found && columnFor(firstRow, match.sourceColumn) !== null)
    .map((match) => match.sourceColumn);
}

/** Fields a new record needs that no column fills. */
export function unmappedRequiredFields(target: OnlineImportTarget | undefined, rows: ImportMappingRow[]): string[] {
  if (!target) return [];
  const mapped = new Set(rows.map((row) => row.targetField).filter(Boolean));
  return target.fields.filter((field) => field.requiredOnCreate && !mapped.has(field.name)).map((field) => field.label);
}

/** No column fills a key field, so every row would create a new record. */
export function noKeyMapped(target: OnlineImportTarget | undefined, rows: ImportMappingRow[]): boolean {
  if (!target || target.keyFields.length === 0) return false;
  const mapped = new Set(rows.map((row) => row.targetField));
  return !target.keyFields.some((key) => mapped.has(key));
}

/** The preview can run: a read file within the cap, a mapping, and a right to write. */
export function canPreview(state: OnlineImportState, target: OnlineImportTarget | undefined): boolean {
  return !!state.file
    && !!state.analysis
    && !state.analysis.exceedsMaxRows
    && mappingsToSend(state.mappings).length > 0
    && !!target
    && (target.canCreate || target.canUpdate);
}

/** The import can start: the preview of this very file, format and mapping, with a row that would go in. */
export function canImport(state: OnlineImportState, target: OnlineImportTarget | undefined): boolean {
  return canPreview(state, target) && rowsToImport(state.preview) > 0;
}

/** One column of the preview: a field the mapping fills, and the file's column it is read from. */
export interface PreviewColumn {
  field: string;
  label: string;
  sourceColumn: string;
  transformChain: string | null;
  isKey: boolean;
}

/** The preview's columns: each field the mapping fills, once, the key fields first, the others in the mapping's order. */
export function previewColumns(target: OnlineImportTarget, mappings: OnlineImportMapping[]): PreviewColumn[] {
  const byField = new Map<string, PreviewColumn>();
  mappings.forEach(({ targetField, sourceColumn, transformChain }) => {
    if (!targetField) return;
    byField.set(targetField, {
      field: targetField,
      label: target.fields.find((f) => f.name === targetField)?.label ?? targetField,
      sourceColumn,
      transformChain: transformChain || null,
      isKey: target.keyFields.includes(targetField),
    });
  });
  const columns = [...byField.values()];
  return [...columns.filter((column) => column.isKey), ...columns.filter((column) => !column.isKey)];
}

/** The rows an import of the previewed file would write: those that create a record or update one. None before a preview. */
export function rowsToImport(preview: OnlineImportPreview | null): number {
  return preview ? preview.creates + preview.updates : 0;
}
